import { useCallback } from 'react';

import { NotificationActions } from 'domains/notifications/store/NotificationActions';
import { isMutationSuccessful } from 'modules/common/utils/isMutationSuccessful';
import { referralProgramTranslation } from 'modules/referralProgram/translation';
import { removeReferralCodeFromUrl } from 'modules/referralProgram/utils/removeReferralCodeFromUrl';
import { useAppDispatch } from 'store/useAppDispatch';
import { useApplyReferralCodeMutation } from 'modules/referralProgram/actions/applyReferralCode';
import { useReferralProgram } from 'modules/referralProgram/hooks/useReferralProgram';
import { useSavedReferralCode } from 'modules/referralProgram/hooks/useSavedReferralCode';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { useLazyFetchReferrerQuery } from '../actions/fetchReferrer';

export interface IUseApplyReferralCodeProps {
  hasSuccessNotification?: boolean;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  referralCode: string | undefined;
  shouldRemoveSavedData?: boolean;
}

const { showNotification } = NotificationActions;

export const useApplyReferralCode = ({
  hasSuccessNotification = true,
  onError,
  onSuccess,
  referralCode,
  shouldRemoveSavedData = true,
}: IUseApplyReferralCodeProps) => {
  const [applyReferralCode, { isLoading: isApplying, isSuccess: isApplied }] =
    useApplyReferralCodeMutation();

  const [handleFetchReferrer] = useLazyFetchReferrerQuery();

  const { handleRemoveSavedReferralCode } = useSavedReferralCode();

  const { referralProgram } = useReferralProgram();

  const {
    keys: { branded, unbranded },
    t,
  } = useTranslation(referralProgramTranslation);

  const keys = referralProgram ? branded : unbranded;

  const dispatch = useAppDispatch();

  const referralProgramName = referralProgram?.name;

  const handleApplyReferralCode = useCallback(async () => {
    if (referralCode) {
      const response = await applyReferralCode({ code: referralCode });

      if (isMutationSuccessful(response)) {
        if (hasSuccessNotification) {
          dispatch(
            showNotification({
              message: t(keys.activationAcceptedMessage, {
                referralProgramName,
              }),
              severity: 'success',
              title: t(keys.activationAcceptedTitle, { referralProgramName }),
            }),
          );
        }

        await handleFetchReferrer();

        onSuccess?.();
      } else {
        onError?.(response.error);
      }

      if (shouldRemoveSavedData) {
        removeReferralCodeFromUrl();
        handleRemoveSavedReferralCode();
      }
    }
  }, [
    applyReferralCode,
    dispatch,
    handleFetchReferrer,
    handleRemoveSavedReferralCode,
    hasSuccessNotification,
    keys,
    onError,
    onSuccess,
    referralCode,
    referralProgramName,
    shouldRemoveSavedData,
    t,
  ]);

  return { handleApplyReferralCode, isApplied, isApplying };
};
